import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import { reviewsService } from '../services/reviewsService';

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------
const CHART_COLORS = [
  '#c9a84c', '#6dbb88', '#88aaee', '#dd9944', '#cc7777',
  '#88ccaa', '#ddbb66', '#aa88cc', '#66bbdd', '#dd8866',
  '#f0c040', '#4ecdc4', '#e05454', '#9b59b6', '#e67e22',
];

const DARK = {
  card: '#111008',
  border: '#3a2e00',
  gold: '#c9a84c',
  goldBright: '#f0c040',
  muted: '#7a6a40',
  mutedLight: '#9a8a60',
};

// ---------------------------------------------------------------------------
// Period utilities  (self-contained copy so this file has no cross-component deps)
// ---------------------------------------------------------------------------
const PERIOD_RE = /(Q[1-4]_\d{4}|H[1-2]_\d{4}|FY_?\d{4})/i;

function extractPeriodInfo(fieldName) {
  const upper = fieldName.toUpperCase();
  const m = upper.match(PERIOD_RE);
  if (!m) return null;
  const period = m[1].replace(/FY(\d{4})$/, 'FY_$1');
  const base = upper
    .replace(m[1], '')
    .replace(/__+/g, '_')
    .replace(/^_+|_+$/g, '');
  return { period, base };
}

function sortKeyForPeriod(p) {
  const q = p.match(/^Q([1-4])_(\d{4})$/i);
  if (q) return parseInt(q[2]) * 100 + parseInt(q[1]);
  const h = p.match(/^H([12])_(\d{4})$/i);
  if (h) return parseInt(h[2]) * 100 + (parseInt(h[1]) === 1 ? 20 : 60);
  const fy = p.match(/^FY_?(\d{4})$/i);
  if (fy) return parseInt(fy[1]) * 100 + 80;
  return 0;
}

function formatPeriodLabel(period) {
  const q = period.match(/^Q([1-4])_(\d{4})$/i);
  if (q) return `Q${q[1]} '${q[2].slice(2)}`;
  const h = period.match(/^H([12])_(\d{4})$/i);
  if (h) return `H${h[1]} '${h[2].slice(2)}`;
  const fy = period.match(/^FY_?(\d{4})$/i);
  if (fy) return `FY '${fy[1].slice(2)}`;
  return period;
}

function parseValue(val) {
  if (val === null || val === undefined || val === '') return null;
  const s = String(val).replace(/[$,\s~≈]/g, '').replace(/^[><]/g, '');
  if (/^-?[\d.]+[Bb]$/.test(s)) return parseFloat(s) * 1000;
  if (/^-?[\d.]+[Mm]$/.test(s)) return parseFloat(s);
  if (/^-?[\d.]+[Kk]$/.test(s)) return parseFloat(s) / 1000;
  const n = parseFloat(s.replace(/%$/, '').replace(/[xX]$/, ''));
  return isNaN(n) ? null : n;
}

function flattenRecordFields(fields) {
  const flat = {};
  for (const [sectionKey, sectionVal] of Object.entries(fields || {})) {
    if (sectionKey === '_meta') continue;
    if (sectionKey === '_root') {
      if (sectionVal && typeof sectionVal === 'object') Object.assign(flat, sectionVal);
      continue;
    }
    if (sectionVal && typeof sectionVal === 'object' && !Array.isArray(sectionVal)) {
      Object.assign(flat, sectionVal);
    } else {
      flat[sectionKey] = sectionVal;
    }
  }
  return flat;
}

// ---------------------------------------------------------------------------
// Display label utilities
// Strips trailing scale/accounting qualifiers so each variant gets a
// distinct readable name:
//   ADJUSTED_NET_INCOME_MILLIONS → "Adj. Net Income ($M)"
//   ADJUSTED_NET_INCOME_NON_GAAP → "Adj. Net Income (Non-GAAP)"
//   ADJUSTED_NET_INCOME          → "Adj. Net Income"
// ---------------------------------------------------------------------------
const DISPLAY_SUFFIXES = [
  [/_IN_MILLIONS$/, '($M)'],
  [/_MILLIONS$/, '($M)'],
  [/_IN_THOUSANDS$/, '($K)'],
  [/_THOUSANDS$/, '($K)'],
  [/_IN_BILLIONS$/, '($B)'],
  [/_BILLIONS$/, '($B)'],
  [/_NON_GAAP$/, '(Non-GAAP)'],
  [/_GAAP$/, '(GAAP)'],
  [/_PER_SHARE_DILUTED$/, '/ Share (Dil.)'],
  [/_PER_SHARE_BASIC$/, '/ Share (Basic)'],
  [/_PER_SHARE$/, '/ Share'],
  [/_DILUTED$/, '(Dil.)'],
  [/_BASIC$/, '(Basic)'],
];

const LABEL_SUBS = [
  [/^PLATFORM_AND_REFERRAL_FEES_NET$/, 'Platform/Referral Fees'],
  [/^REVENUE_FROM_FEES_NET$/, 'Revenue from Fees'],
  [/^TOTAL_REVENUE$/, 'Total Revenue'],
  [/^NET_REVENUE$/, 'Net Revenue'],
  [/^ADJUSTED_EBITDA_MARGIN$/, 'Adj. EBITDA Margin'],
  [/^ADJUSTED_EBITDA$/, 'Adj. EBITDA'],
  [/^CONTRIBUTION_PROFIT_MARGIN$/, 'Contribution Profit Margin'],
  [/^CONTRIBUTION_PROFIT$/, 'Contribution Profit'],
  [/^CONTRIBUTION_MARGIN$/, 'Contribution Margin'],
  [/^ADJUSTED_NET_INCOME_LOSS$/, 'Adj. Net Income/Loss'],
  [/^ADJUSTED_NET_INCOME$/, 'Adj. Net Income'],
  [/^ADJUSTED_NET_LOSS$/, 'Adj. Net Loss'],
  [/^NET_INCOME_LOSS$/, 'Net Income/Loss'],
  [/^NET_INCOME$/, 'Net Income'],
  [/^NET_LOSS$/, 'Net Loss'],
  [/^OPERATING_INCOME_LOSS$/, 'Operating Income/Loss'],
  [/^OPERATING_INCOME$/, 'Operating Income'],
  [/^PERSONAL_LOAN_ORIGINATIONS$/, 'PL Originations'],
  [/^AUTO_ORIGINATIONS$/, 'Auto Originations'],
  [/^HOME_ORIGINATIONS$/, 'Home Originations'],
  [/^AUTO_NUMBER_OF_LOANS$/, 'Auto Loan Count'],
  [/^PERSONAL_LOAN_NUMBER_OF_LOANS$/, 'PL Loan Count'],
  [/^PERSONAL_LOAN_CORE_ORIGINATIONS$/, 'PL Core Originations'],
  [/^PERSONAL_LOAN_SUPER_PRIME_ORIGINATIONS$/, 'PL Super-Prime Orig.'],
  [/^SALES_AND_MARKETING$/, 'Sales & Marketing'],
  [/^GENERAL_AND_ADMINISTRATIVE$/, 'G&A'],
  [/^RESEARCH_AND_DEVELOPMENT$/, 'R&D'],
  [/^OPERATING_EXPENSES$/, 'Operating Expenses'],
  [/^CONVERSION_RATE$/, 'Conversion Rate'],
];

function shortLabel(base) {
  // Strip trailing qualifiers; remember them to append to the display name
  let core = base;
  const qualifiers = [];
  let changed = true;
  while (changed) {
    changed = false;
    for (const [pattern, label] of DISPLAY_SUFFIXES) {
      if (pattern.test(core)) {
        core = core.replace(pattern, '');
        qualifiers.unshift(label);
        changed = true;
        break;
      }
    }
  }
  // Match the stripped core against human-readable names
  let mainLabel = null;
  for (const [pattern, label] of LABEL_SUBS) {
    if (pattern.test(core)) { mainLabel = label; break; }
  }
  // Fallback: title-case the core key
  if (!mainLabel) {
    mainLabel = core
      .replace(/_PCT$/, ' %')
      .replace(/_PERCENT$/, ' %')
      .replace(/_NET$/, '')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return qualifiers.length ? `${mainLabel} ${qualifiers.join(' ')}` : mainLabel;
}

function opLabel(op) {
  return op === '/' ? '÷' : op;
}

function applyOp(a, b, op) {
  if (op === '+') return a + b;
  if (op === '-') return a - b;
  if (op === '/') return b !== 0 ? a / b : null;
  return null;
}

// Evaluate a single term node against a data row.
// term.type === 'leaf': single metric option {value, label}
// term.type === 'group': {operands: [option|null,...], operators: [op,...]}
function evalTermNode(row, term) {
  if (term.type === 'leaf') return term.value ? (row[term.value.value] ?? null) : null;
  let r = term.operands[0] ? (row[term.operands[0].value] ?? null) : null;
  for (let i = 0; i < term.operators.length; i++) {
    const b = term.operands[i + 1] ? (row[term.operands[i + 1].value] ?? null) : null;
    if (r === null || b === null) return null;
    r = applyOp(r, b, term.operators[i]);
  }
  return r;
}

// Evaluate a full computed metric formula against a data row.
function evalFormula(row, cm) {
  let r = evalTermNode(row, cm.terms[0]);
  for (let i = 0; i < cm.termOps.length; i++) {
    const b = evalTermNode(row, cm.terms[i + 1]);
    if (r === null || b === null) return null;
    r = applyOp(r, b, cm.termOps[i]);
  }
  return r;
}

// Return all leaf metric option objects in document order.
function flatLeaves(terms) {
  return terms.flatMap((t) =>
    t.type === 'leaf' ? (t.value ? [t.value] : []) : t.operands.filter(Boolean)
  );
}

// True when every operator in the formula is '+' (enables stacking).
function isAllSum(cm) {
  return (
    cm.termOps.every((op) => op === '+') &&
    cm.terms.every((t) => t.type === 'leaf' || t.operators.every((op) => op === '+'))
  );
}

// Build a human-readable expression string, with parens around groups.
function formatExpr(terms, termOps) {
  return terms
    .map((t, i) => {
      const str =
        t.type === 'leaf'
          ? (t.value ? shortLabel(t.value.value) : '?')
          : '(' +
            t.operands
              .map((o, j) =>
                j === 0
                  ? (o ? shortLabel(o.value) : '?')
                  : ` ${opLabel(t.operators[j - 1])} ${o ? shortLabel(o.value) : '?'}`
              )
              .join('') +
            ')';
      return i === 0 ? str : ` ${opLabel(termOps[i - 1])} ${str}`;
    })
    .join('');
}

// ---------------------------------------------------------------------------
// Series map builder
// ---------------------------------------------------------------------------

/**
 * Derive a quarter period string (e.g. "Q4_2025") from an ISO date string.
 * Returns null if the date cannot be parsed.
 */
function periodFromDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const q = Math.ceil((d.getMonth() + 1) / 3);
  return `Q${q}_${d.getFullYear()}`;
}

function buildSeriesMap(records) {
  const seriesMap = {};
  // Process each record individually so we can fall back to its period_end_date
  // for records where field names don't embed a period token (new flat format).
  for (const rec of [...records].reverse()) {
    const flat = flattenRecordFields(rec.fields);
    const fallbackPeriod = periodFromDate(rec.period_end_date);
    for (const [fieldName, rawVal] of Object.entries(flat)) {
      const info = extractPeriodInfo(fieldName);
      let base, period;
      if (info) {
        base = info.base;
        period = info.period;
      } else if (fallbackPeriod) {
        base = fieldName.toUpperCase();
        period = fallbackPeriod;
      } else {
        continue;
      }
      const num = parseValue(rawVal);
      if (num === null) continue;
      if (!seriesMap[base]) seriesMap[base] = {};
      seriesMap[base][period] = num;
    }
  }
  return mergeCompatibleSeries(seriesMap);
}

// ---------------------------------------------------------------------------
// Unit-suffix merge
// Bases differing only by _MILLIONS / _THOUSANDS etc. are the same metric
// labelled differently. Merge them if their values on shared periods are
// within the same order of magnitude (ratio ≤10×). If values diverge more
// than that, keep them separate (genuine unit mismatch).
// ---------------------------------------------------------------------------
function stripUnitSuffix(base) {
  return base.replace(
    /_(IN_MILLIONS|IN_THOUSANDS|IN_BILLIONS|MILLIONS|THOUSANDS|BILLIONS|MM|USD)$/,
    ''
  );
}

function mergeCompatibleSeries(seriesMap) {
  const groups = {};
  for (const base of Object.keys(seriesMap)) {
    const stripped = stripUnitSuffix(base);
    if (!groups[stripped]) groups[stripped] = [];
    groups[stripped].push(base);
  }

  const result = { ...seriesMap };

  for (const [canonical, bases] of Object.entries(groups)) {
    if (bases.length < 2) continue;

    const allPeriods = [...new Set(bases.flatMap((b) => Object.keys(seriesMap[b])))];
    let hasConflict = false;
    for (const period of allPeriods) {
      const vals = bases
        .map((b) => seriesMap[b][period])
        .filter((v) => v !== undefined)
        .map(Math.abs)
        .filter((v) => v > 0);
      if (vals.length < 2) continue;
      const ratio = Math.max(...vals) / Math.min(...vals);
      if (ratio > 10) { hasConflict = true; break; }
    }
    if (hasConflict) continue;

    const merged = {};
    for (const base of bases) Object.assign(merged, seriesMap[base]);
    if (seriesMap[canonical]) Object.assign(merged, seriesMap[canonical]);

    for (const base of bases) delete result[base];
    result[canonical] = merged;
  }

  return result;
}

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------
function aggregateData(seriesMap, bases, granularity) {
  if (bases.length === 0) return [];

  const allPeriods = new Set(bases.flatMap((b) => Object.keys(seriesMap[b] || {})));

  if (granularity === 'quarters') {
    return [...allPeriods]
      .filter((p) => /^Q[1-4]_\d{4}$/i.test(p))
      .sort((a, b) => sortKeyForPeriod(a) - sortKeyForPeriod(b))
      .map((period) => {
        const row = { period, periodLabel: formatPeriodLabel(period) };
        for (const base of bases) {
          const val = seriesMap[base]?.[period];
          if (val !== undefined) row[base] = val;
        }
        return row;
      })
      .filter((row) => bases.some((b) => row[b] !== undefined));
  }

  if (granularity === 'halves') {
    const years = new Set(
      [...allPeriods].map((p) => p.match(/(\d{4})$/)?.[1]).filter(Boolean)
    );
    const rows = [];
    for (const year of [...years].sort()) {
      for (const half of ['H1', 'H2']) {
        const halfKey = `${half}_${year}`;
        const row = { period: halfKey, periodLabel: formatPeriodLabel(halfKey) };
        for (const base of bases) {
          const sm = seriesMap[base] || {};
          if (sm[halfKey] !== undefined) {
            row[base] = sm[halfKey];
          } else {
            const qtrs = half === 'H1'
              ? [`Q1_${year}`, `Q2_${year}`]
              : [`Q3_${year}`, `Q4_${year}`];
            const vals = qtrs.map((q) => sm[q]).filter((v) => v !== undefined);
            if (vals.length > 0) row[base] = vals.reduce((s, v) => s + v, 0) / vals.length;
          }
        }
        if (bases.some((b) => row[b] !== undefined)) rows.push(row);
      }
    }
    return rows;
  }

  if (granularity === 'years') {
    const years = new Set(
      [...allPeriods].map((p) => p.match(/(\d{4})$/)?.[1]).filter(Boolean)
    );
    return [...years]
      .sort()
      .map((year) => {
        const fyKey = `FY_${year}`;
        const row = { period: fyKey, periodLabel: `FY '${year.slice(2)}` };
        for (const base of bases) {
          const sm = seriesMap[base] || {};
          const native = sm[fyKey] ?? sm[`FY${year}`];
          if (native !== undefined) {
            row[base] = native;
          } else {
            const quarters = ['Q1', 'Q2', 'Q3', 'Q4']
              .map((q) => sm[`${q}_${year}`])
              .filter((v) => v !== undefined);
            const halves = ['H1', 'H2']
              .map((h) => sm[`${h}_${year}`])
              .filter((v) => v !== undefined);
            const useVals = quarters.length > 0 ? quarters : halves;
            if (useVals.length > 0)
              row[base] = useVals.reduce((s, v) => s + v, 0) / useVals.length;
          }
        }
        if (!bases.some((b) => row[b] !== undefined)) return null;
        return row;
      })
      .filter(Boolean);
  }

  return [];
}

// ---------------------------------------------------------------------------
// react-select dark styles
// ---------------------------------------------------------------------------
const SELECT_STYLES = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#1a1400',
    borderColor: state.isFocused ? DARK.gold : DARK.border,
    boxShadow: 'none',
    minHeight: 30,
    fontSize: '0.75rem',
    cursor: 'pointer',
    '&:hover': { borderColor: '#5a4a00' },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#1a1400',
    border: `1px solid ${DARK.border}`,
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#3a2e00' : state.isFocused ? '#2a2000' : 'transparent',
    color: state.isSelected ? DARK.goldBright : DARK.gold,
    fontSize: '0.73rem',
    padding: '5px 10px',
    cursor: 'pointer',
  }),
  multiValue: (provided) => ({ ...provided, backgroundColor: '#3a2e00', borderRadius: 4 }),
  multiValueLabel: (provided) => ({ ...provided, color: DARK.goldBright, fontSize: '0.7rem' }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: DARK.mutedLight,
    ':hover': { backgroundColor: '#5a4a00', color: '#fff' },
  }),
  placeholder: (provided) => ({ ...provided, color: '#5a4a30', fontSize: '0.73rem' }),
  input: (provided) => ({ ...provided, color: DARK.goldBright, fontSize: '0.73rem' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#5a4a30',
    padding: '0 6px',
    '&:hover': { color: DARK.gold },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: '#5a4a30',
    padding: '0 4px',
    '&:hover': { color: DARK.gold },
  }),
  valueContainer: (provided) => ({ ...provided, padding: '2px 6px', flexWrap: 'wrap', gap: 2 }),
};

// ---------------------------------------------------------------------------
// Custom tooltip
// ---------------------------------------------------------------------------
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const valid = payload.filter((p) => p.value !== undefined && p.value !== null);
  if (!valid.length) return null;
  return (
    <div
      style={{
        backgroundColor: '#111008',
        border: `1px solid ${DARK.border}`,
        padding: '8px 12px',
        borderRadius: 6,
        fontSize: '0.75rem',
      }}
    >
      <div style={{ color: DARK.goldBright, fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {valid.map((p, i) => (
        <div key={i} style={{ color: p.color, marginBottom: 2 }}>
          <span style={{ color: DARK.mutedLight }}>{p.name || shortLabel(p.dataKey)}: </span>
          <span style={{ fontWeight: 600 }}>
            {typeof p.value === 'number'
              ? p.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function CustomEarningsChart({ records, companyId }) {
  const [collapsed, setCollapsed] = useState(false);
  const [granularity, setGranularity] = useState('quarters');
  // selectedMetrics: [{value, label, type: 'bar'|'line', yAxis: 'left'|'right'}]
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [defaultType, setDefaultType] = useState('bar');
  const [stacked, setStacked] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  // computedMetrics: [{id, label, terms, termOps, type, yAxis, stackComponents}]
  // terms: [{type:'leaf', value:option|null} | {type:'group', operands:[option|null,...], operators:[op,...]}]
  const [computedMetrics, setComputedMetrics] = useState([]);
  // transient formula builder state
  const [showFormula, setShowFormula] = useState(false);
  const [fTerms, setFTerms] = useState([{ type: 'leaf', value: null }, { type: 'leaf', value: null }]);
  const [fTermOps, setFTermOps] = useState(['+']);
  const [fLabel, setFLabel] = useState('');
  const [fType, setFType] = useState('bar');

  // Saved charts state
  const [savedCharts, setSavedCharts] = useState([]);
  const [activeChartId, setActiveChartId] = useState(null);   // currently loaded chart id
  const [activeChartName, setActiveChartName] = useState(''); // name of currently loaded chart
  const [showSaveRow, setShowSaveRow] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [chartsLoading, setChartsLoading] = useState(false);

  // Load saved charts whenever companyId changes
  const fetchSavedCharts = useCallback(async () => {
    if (!companyId) return;
    setChartsLoading(true);
    try {
      const charts = await reviewsService.listCharts(companyId);
      setSavedCharts(charts);
    } catch (_err) {
      // silently ignore load failure
    } finally {
      setChartsLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchSavedCharts(); }, [fetchSavedCharts]);

  // Apply a saved chart config to local state
  const applyChartConfig = useCallback((chart) => {
    const cfg = chart.config || {};
    setSelectedMetrics(cfg.selectedMetrics || []);
    setComputedMetrics(cfg.computedMetrics || []);
    setGranularity(cfg.granularity || 'quarters');
    setStacked(cfg.stacked || false);
    setDefaultType(cfg.defaultType || 'bar');
    setActiveChartId(chart.chart_id);
    setActiveChartName(chart.name);
    setShowSaveRow(false);
  }, []);

  // Build the config snapshot from current state
  const buildConfig = () => ({
    selectedMetrics,
    computedMetrics,
    granularity,
    stacked,
    defaultType,
  });

  const handleSaveNew = async () => {
    if (!companyId || !saveName.trim()) return;
    setSaving(true);
    try {
      const created = await reviewsService.createChart(companyId, saveName.trim(), buildConfig());
      setSavedCharts((prev) => [created, ...prev]);
      setActiveChartId(created.chart_id);
      setActiveChartName(created.name);
      setShowSaveRow(false);
      setSaveName('');
    } catch (_err) {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!companyId || !activeChartId || !saveName.trim()) return;
    setSaving(true);
    try {
      const updated = await reviewsService.updateChart(companyId, activeChartId, saveName.trim(), buildConfig());
      setSavedCharts((prev) => prev.map((c) => c.chart_id === activeChartId ? updated : c));
      setActiveChartName(updated.name);
      setShowSaveRow(false);
    } catch (_err) {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChart = async (chartId) => {
    if (!companyId) return;
    try {
      await reviewsService.deleteChart(companyId, chartId);
      setSavedCharts((prev) => prev.filter((c) => c.chart_id !== chartId));
      if (activeChartId === chartId) {
        setActiveChartId(null);
        setActiveChartName('');
      }
    } catch (_err) {
      // ignore
    }
  };

  const savedChartOptions = savedCharts.map((c) => ({
    value: c.chart_id,
    label: c.name,
    chart: c,
  }));

  const seriesMap = useMemo(() => {
    if (!records?.length) return {};
    return buildSeriesMap(records);
  }, [records]);

  const metricOptions = useMemo(
    () =>
      Object.keys(seriesMap)
        .sort()
        .map((base) => ({ value: base, label: shortLabel(base) })),
    [seriesMap]
  );

  // When the Select changes, preserve existing settings; assign defaults for new ones.
  const handleSelectChange = (newSelections) => {
    const prevByValue = Object.fromEntries(selectedMetrics.map((m) => [m.value, m]));
    setSelectedMetrics(
      (newSelections || []).map((m) => prevByValue[m.value] || { ...m, type: defaultType, yAxis: 'left' })
    );
  };

  // Toggle a single metric between bar and line
  const toggleMetricType = (value, type) => {
    setSelectedMetrics((prev) =>
      prev.map((m) => (m.value === value ? { ...m, type } : m))
    );
  };

  // Toggle a single metric's Y-axis side
  const toggleMetricAxis = (value) => {
    setSelectedMetrics((prev) =>
      prev.map((m) => (m.value === value ? { ...m, yAxis: m.yAxis === 'right' ? 'left' : 'right' } : m))
    );
  };

  // Remove a single metric
  const removeMetric = (value) => {
    setSelectedMetrics((prev) => prev.filter((m) => m.value !== value));
  };

  // --- Computed metric handlers ---
  const addFormula = () => {
    const leaves = fTerms.flatMap((t) => t.type === 'leaf' ? [t.value] : t.operands);
    if (!leaves.every(Boolean)) return;
    const id = `__f_${Date.now()}`;
    const autoLabel = formatExpr(fTerms, fTermOps);
    setComputedMetrics((prev) => [
      ...prev,
      { id, label: fLabel.trim() || autoLabel, terms: fTerms, termOps: fTermOps, type: fType, yAxis: 'left', stackComponents: false },
    ]);
    setFTerms([{ type: 'leaf', value: null }, { type: 'leaf', value: null }]);
    setFTermOps(['+']); setFLabel(''); setShowFormula(false);
  };

  const removeComputedMetric = (id) =>
    setComputedMetrics((prev) => prev.filter((cm) => cm.id !== id));

  const toggleComputedType = (id, type) =>
    setComputedMetrics((prev) => prev.map((cm) => cm.id === id ? { ...cm, type } : cm));

  const toggleComputedAxis = (id) =>
    setComputedMetrics((prev) =>
      prev.map((cm) => cm.id === id ? { ...cm, yAxis: cm.yAxis === 'right' ? 'left' : 'right' } : cm)
    );

  const toggleStackComponents = (id) =>
    setComputedMetrics((prev) =>
      prev.map((cm) => cm.id === id ? { ...cm, stackComponents: !cm.stackComponents } : cm)
    );

  const hasAnyBar =
    selectedMetrics.some((m) => m.type === 'bar') ||
    computedMetrics.some((cm) => cm.type === 'bar' || (cm.stackComponents && isAllSum(cm)));
  const hasRightAxis =
    selectedMetrics.some((m) => m.yAxis === 'right') ||
    computedMetrics.some((cm) => cm.yAxis === 'right');

  const chartData = useMemo(() => {
    const regularBases = selectedMetrics.map((m) => m.value);
    const extraBases = computedMetrics.flatMap((cm) => flatLeaves(cm.terms).map((o) => o.value));
    const allBases = [...new Set([...regularBases, ...extraBases])];
    const rows = aggregateData(seriesMap, allBases, granularity);
    for (const row of rows) {
      for (const cm of computedMetrics) {
        if (cm.stackComponents && isAllSum(cm)) {
          flatLeaves(cm.terms).forEach((o, i) => { row[`__s${i}_${cm.id}`] = row[o.value] ?? null; });
        } else {
          row[cm.id] = evalFormula(row, cm);
        }
      }
    }
    return rows;
  }, [seriesMap, selectedMetrics, computedMetrics, granularity]);

  const hasNegative = chartData.some((row) =>
    selectedMetrics.filter((m) => m.yAxis !== 'right').some((m) => typeof row[m.value] === 'number' && row[m.value] < 0) ||
    computedMetrics.filter((cm) => cm.yAxis !== 'right').some((cm) => {
      if (cm.stackComponents && isAllSum(cm))
        return flatLeaves(cm.terms).some((o, i) => typeof row[`__s${i}_${cm.id}`] === 'number' && row[`__s${i}_${cm.id}`] < 0);
      return typeof row[cm.id] === 'number' && row[cm.id] < 0;
    })
  );
  const hasNegativeRight = chartData.some((row) =>
    selectedMetrics.filter((m) => m.yAxis === 'right').some((m) => typeof row[m.value] === 'number' && row[m.value] < 0) ||
    computedMetrics.filter((cm) => cm.yAxis === 'right').some((cm) => {
      if (cm.stackComponents && isAllSum(cm))
        return flatLeaves(cm.terms).some((o, i) => typeof row[`__s${i}_${cm.id}`] === 'number' && row[`__s${i}_${cm.id}`] < 0);
      return typeof row[cm.id] === 'number' && row[cm.id] < 0;
    })
  );

  const yFormatter = (v) => {
    if (typeof v !== 'number') return v;
    if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}B`;
    if (Math.abs(v) >= 100) return v.toFixed(0);
    if (Math.abs(v) >= 10) return v.toFixed(1);
    return v.toFixed(2);
  };

  const pillBtn = (active) => ({
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '3px 12px',
    borderRadius: 20,
    border: `1px solid ${DARK.border}`,
    cursor: 'pointer',
    backgroundColor: active ? DARK.gold : '#1e1800',
    color: active ? '#000' : DARK.muted,
    transition: 'all 0.15s',
  });

  const microBtn = (active, color) => ({
    fontSize: '0.62rem',
    fontWeight: 600,
    padding: '1px 6px',
    borderRadius: 10,
    border: `1px solid ${active ? color : DARK.border}`,
    cursor: 'pointer',
    backgroundColor: active ? color : 'transparent',
    color: active ? '#000' : DARK.muted,
    lineHeight: 1.6,
    transition: 'all 0.15s',
  });

  const divider = (
    <div style={{ width: 1, height: 24, backgroundColor: DARK.border, flexShrink: 0 }} />
  );

  const renderChart = (height) => (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={chartData} margin={{ top: 8, right: hasRightAxis ? 52 : 16, bottom: 4, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#2a2000" vertical={false} />
        <XAxis dataKey="periodLabel" tick={{ fill: DARK.muted, fontSize: 10 }} tickLine={false} axisLine={{ stroke: DARK.border }} />
        <YAxis yAxisId="left" orientation="left" tickFormatter={yFormatter} tick={{ fill: DARK.muted, fontSize: 10 }} tickLine={false} axisLine={false} width={44} />
        {hasRightAxis && (
          <YAxis yAxisId="right" orientation="right" tickFormatter={yFormatter} tick={{ fill: DARK.mutedLight, fontSize: 10 }} tickLine={false} axisLine={false} width={44} />
        )}
        <Tooltip content={<ChartTooltip />} />
        {hasNegative && <ReferenceLine yAxisId="left" y={0} stroke="#5a4a00" strokeDasharray="4 2" />}
        {hasNegativeRight && <ReferenceLine yAxisId="right" y={0} stroke="#5a4a00" strokeDasharray="4 2" />}
        {selectedMetrics.map((m, i) => {
          const color = CHART_COLORS[i % CHART_COLORS.length];
          const axisId = m.yAxis === 'right' ? 'right' : 'left';
          return m.type === 'bar' ? (
            <Bar key={m.value} yAxisId={axisId} dataKey={m.value} name={m.label} fill={color}
                 stackId={stacked ? `stack-${axisId}` : undefined}
                 radius={stacked ? [0, 0, 0, 0] : [3, 3, 0, 0]} maxBarSize={stacked ? undefined : 52} />
          ) : (
            <Line key={m.value} yAxisId={axisId} type="monotone" dataKey={m.value} name={m.label}
                  stroke={color} strokeWidth={2} dot={{ fill: color, r: 3 }} activeDot={{ r: 5 }} connectNulls />
          );
        })}
        {computedMetrics.flatMap((cm, ci) => {
          const colorIdx = selectedMetrics.length + ci;
          const axisId = cm.yAxis === 'right' ? 'right' : 'left';
          const cmLeaves = flatLeaves(cm.terms);
          if (cm.stackComponents && isAllSum(cm)) {
            return cmLeaves.map((o, oi) => {
              const col = CHART_COLORS[(colorIdx + oi) % CHART_COLORS.length];
              return (
                <Bar key={`${cm.id}_${oi}`} yAxisId={axisId} dataKey={`__s${oi}_${cm.id}`}
                     name={shortLabel(o.value)} fill={col} stackId={`stk_${cm.id}`}
                     radius={oi === cmLeaves.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
              );
            });
          }
          const color = CHART_COLORS[colorIdx % CHART_COLORS.length];
          return [cm.type === 'bar' ? (
            <Bar key={cm.id} yAxisId={axisId} dataKey={cm.id} name={cm.label}
                 fill={color} radius={[3, 3, 0, 0]} maxBarSize={52} />
          ) : (
            <Line key={cm.id} yAxisId={axisId} type="monotone" dataKey={cm.id} name={cm.label}
                  stroke={color} strokeWidth={2} dot={{ fill: color, r: 3 }} activeDot={{ r: 5 }} connectNulls />
          )];
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );

  return (
    <>
      <div style={{ marginBottom: 16 }}>
      {/* ── Section header ── */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          background: '#0c0b02',
          border: `1px solid ${DARK.border}`,
          borderRadius: collapsed ? 7 : '7px 7px 0 0',
          padding: '8px 14px',
          cursor: 'pointer',
          color: DARK.goldBright,
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i
            className={`bi bi-chevron-${collapsed ? 'right' : 'down'}`}
            style={{ fontSize: '0.7rem', color: DARK.muted }}
          />
          <i className="bi bi-sliders" />
          Custom Chart
        </span>
        <button
          type="button"
          title="Fullscreen"
          onClick={(e) => { e.stopPropagation(); setFullscreen(true); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: DARK.muted, fontSize: '0.7rem', padding: '0 4px' }}
        >
          <i className="bi bi-arrows-fullscreen" />
        </button>
      </button>

      {!collapsed && (
        <div
          style={{
            backgroundColor: DARK.card,
            border: `1px solid ${DARK.border}`,
            borderTop: 'none',
            borderRadius: '0 0 7px 7px',
            padding: 14,
          }}
        >
          {/* ── Controls row ── */}
          <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: 10 }}>
            {/* X-axis granularity */}
            <div className="d-flex align-items-center" style={{ gap: 5, flexShrink: 0 }}>
              <span
                style={{
                  fontSize: '0.65rem',
                  color: DARK.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginRight: 2,
                }}
              >
                X Axis
              </span>
              {[
                { value: 'quarters', label: 'Quarters' },
                { value: 'halves', label: 'Halves' },
                { value: 'years', label: 'Years' },
              ].map((g) => (
                <button
                  key={g.value}
                  style={pillBtn(granularity === g.value)}
                  onClick={() => setGranularity(g.value)}
                >
                  {g.label}
                </button>
              ))}
            </div>

            {divider}

            {/* Default type for new metrics */}
            <div className="d-flex align-items-center" style={{ gap: 5, flexShrink: 0 }}>
              <span
                style={{
                  fontSize: '0.65rem',
                  color: DARK.muted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginRight: 2,
                }}
              >
                Type
              </span>
              <button style={pillBtn(defaultType === 'bar')} onClick={() => setDefaultType('bar')}>
                <i className="bi bi-bar-chart-fill me-1" />
                Bar
              </button>
              <button style={pillBtn(defaultType === 'line')} onClick={() => setDefaultType('line')}>
                <i className="bi bi-graph-up me-1" />
                Line
              </button>
            </div>

            {/* Stacked toggle — visible when any bar is selected */}
            {hasAnyBar && (
              <>
                {divider}
                <button
                  style={pillBtn(stacked)}
                  onClick={() => setStacked((s) => !s)}
                >
                  <i className={`bi bi-${stacked ? 'stack' : 'bar-chart-steps'} me-1`} />
                  {stacked ? 'Stacked' : 'Grouped'}
                </button>
              </>
            )}

            {divider}

            {/* Metric multi-select */}
            <div style={{ flex: 1, minWidth: 240 }}>
              <Select
                isMulti
                options={metricOptions}
                value={selectedMetrics}
                onChange={handleSelectChange}
                placeholder="Select metrics to plot..."
                styles={SELECT_STYLES}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                closeMenuOnSelect={false}
              />
            </div>

            {divider}

            {/* Formula series trigger */}
            <button
              title="Add a computed (formula) series"
              style={pillBtn(showFormula)}
              onClick={() => setShowFormula((s) => !s)}
            >
              <i className="bi bi-calculator me-1" />
              Formula
            </button>
          </div>

          {/* ── Saved Charts row ── */}
          {companyId && (
            <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: 8 }}>
              {/* Load dropdown */}
              <div style={{ minWidth: 200, maxWidth: 300 }}>
                <Select
                  options={savedChartOptions}
                  value={savedChartOptions.find((o) => o.value === activeChartId) || null}
                  onChange={(opt) => opt && applyChartConfig(opt.chart)}
                  placeholder={chartsLoading ? 'Loading…' : savedCharts.length === 0 ? 'No saved charts' : 'Load a saved chart…'}
                  isDisabled={chartsLoading || savedCharts.length === 0}
                  isClearable={false}
                  styles={SELECT_STYLES}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              {/* Save / update button */}
              <button
                title={activeChartId ? 'Save or update this chart' : 'Save current chart'}
                style={pillBtn(showSaveRow)}
                onClick={() => {
                  setSaveName(activeChartName);
                  setShowSaveRow((s) => !s);
                }}
              >
                <i className="bi bi-floppy me-1" />
                {activeChartId ? 'Save' : 'Save Chart'}
              </button>

              {/* Delete loaded chart */}
              {activeChartId && !showSaveRow && (
                <button
                  title="Delete this saved chart"
                  style={{ ...pillBtn(false), color: '#cc6666', borderColor: '#5a2222' }}
                  onClick={() => handleDeleteChart(activeChartId)}
                >
                  <i className="bi bi-trash me-1" />
                  Delete
                </button>
              )}

              {/* Loaded chart name badge */}
              {activeChartId && !showSaveRow && (
                <span style={{ fontSize: '0.65rem', color: DARK.muted, fontStyle: 'italic' }}>
                  <i className="bi bi-bookmark-check me-1" style={{ color: DARK.gold }} />
                  {activeChartName}
                </span>
              )}

              {/* Inline save / update input row */}
              {showSaveRow && (
                <div className="d-flex align-items-center" style={{ gap: 6 }}>
                  <input
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder="Chart name…"
                    autoFocus
                    style={{
                      width: 180,
                      backgroundColor: '#111008',
                      border: `1px solid ${DARK.border}`,
                      borderRadius: 4,
                      color: DARK.goldBright,
                      fontSize: '0.72rem',
                      padding: '4px 8px',
                      outline: 'none',
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') activeChartId ? handleUpdate() : handleSaveNew();
                      if (e.key === 'Escape') setShowSaveRow(false);
                    }}
                  />
                  {activeChartId && (
                    <button
                      disabled={saving || !saveName.trim()}
                      style={{ ...pillBtn(false), opacity: saveName.trim() ? 1 : 0.45 }}
                      onClick={handleUpdate}
                    >
                      <i className="bi bi-arrow-repeat me-1" />
                      {saving ? 'Saving…' : 'Update'}
                    </button>
                  )}
                  <button
                    disabled={saving || !saveName.trim()}
                    style={{ ...pillBtn(false), opacity: saveName.trim() ? 1 : 0.45 }}
                    onClick={handleSaveNew}
                  >
                    <i className="bi bi-floppy me-1" />
                    {saving ? 'Saving…' : activeChartId ? 'Save as New' : 'Save'}
                  </button>
                  <button style={pillBtn(false)} onClick={() => setShowSaveRow(false)}>
                    <i className="bi bi-x me-1" />
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Formula builder ── */}
          {showFormula && (
            <div
              style={{
                padding: '10px 12px',
                marginBottom: 8,
                backgroundColor: '#1a1400',
                border: `1px solid ${DARK.border}`,
                borderRadius: 6,
              }}
            >
              {/* Term chain row */}
              <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                <span style={{ fontSize: '0.65rem', color: DARK.muted, letterSpacing: '0.06em', textTransform: 'uppercase', flexShrink: 0, marginTop: 6 }}>
                  Formula
                </span>
                {fTerms.map((term, tIdx) => (
                  <React.Fragment key={tIdx}>
                    {/* Outer operator between terms */}
                    {tIdx > 0 && (
                      <div style={{ display: 'flex', gap: 2, flexShrink: 0, alignSelf: 'flex-start', marginTop: 5 }}>
                        {['+', '-', '/'].map((op) => (
                          <button
                            key={op}
                            style={microBtn(fTermOps[tIdx - 1] === op, DARK.gold)}
                            onClick={() => setFTermOps((prev) => prev.map((o, i) => i === tIdx - 1 ? op : o))}
                          >
                            {op === '/' ? '÷' : op}
                          </button>
                        ))}
                      </div>
                    )}
                    {term.type === 'leaf' ? (
                      /* Leaf term */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                        <div style={{ width: 148 }}>
                          <Select
                            options={metricOptions}
                            value={term.value}
                            onChange={(v) => setFTerms((prev) => prev.map((t, i) => i === tIdx ? { ...t, value: v } : t))}
                            placeholder={`Metric ${tIdx + 1}…`}
                            styles={SELECT_STYLES}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isClearable
                          />
                        </div>
                        <button
                          title="Wrap in parentheses"
                          style={{ ...microBtn(false, DARK.mutedLight), fontSize: '0.58rem', alignSelf: 'center', letterSpacing: '0.04em' }}
                          onClick={() => setFTerms((prev) => prev.map((t, i) => i === tIdx ? { type: 'group', operands: [t.value, null], operators: ['+'] } : t))}
                        >
                          ( )
                        </button>
                      </div>
                    ) : (
                      /* Group term — parenthesized sub-expression */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, border: `1px solid #5a4a00`, borderRadius: 6, padding: '5px 8px', backgroundColor: '#111008', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                          <span style={{ color: '#7a6a40', fontSize: '0.72rem', fontWeight: 700 }}>(</span>
                          {term.operands.map((op, opIdx) => (
                            <React.Fragment key={opIdx}>
                              {opIdx > 0 && (
                                <div style={{ display: 'flex', gap: 2 }}>
                                  {['+', '-', '/'].map((o) => (
                                    <button
                                      key={o}
                                      style={microBtn(term.operators[opIdx - 1] === o, DARK.gold)}
                                      onClick={() => setFTerms((prev) => prev.map((t, i) => i === tIdx ? { ...t, operators: t.operators.map((oo, oi) => oi === opIdx - 1 ? o : oo) } : t))}
                                    >
                                      {o === '/' ? '÷' : o}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <div style={{ width: 138 }}>
                                <Select
                                  options={metricOptions}
                                  value={op}
                                  onChange={(v) => setFTerms((prev) => prev.map((t, i) => i === tIdx ? { ...t, operands: t.operands.map((o, oi) => oi === opIdx ? v : o) } : t))}
                                  placeholder="Metric…"
                                  styles={SELECT_STYLES}
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  isClearable
                                />
                              </div>
                            </React.Fragment>
                          ))}
                          <span style={{ color: '#7a6a40', fontSize: '0.72rem', fontWeight: 700 }}>)</span>
                          {term.operands.length < 4 && (
                            <button title="Add metric inside group" style={{ ...microBtn(false, DARK.gold), padding: '1px 6px' }}
                              onClick={() => setFTerms((prev) => prev.map((t, i) => i === tIdx ? { ...t, operands: [...t.operands, null], operators: [...t.operators, '+'] } : t))}>
                              <i className="bi bi-plus" />
                            </button>
                          )}
                          {term.operands.length > 2 && (
                            <button title="Remove last from group" style={{ ...microBtn(false, DARK.gold), padding: '1px 6px' }}
                              onClick={() => setFTerms((prev) => prev.map((t, i) => i === tIdx ? { ...t, operands: t.operands.slice(0, -1), operators: t.operators.slice(0, -1) } : t))}>
                              <i className="bi bi-dash" />
                            </button>
                          )}
                        </div>
                        <button
                          title="Remove parentheses"
                          style={{ ...microBtn(false, DARK.mutedLight), fontSize: '0.58rem', alignSelf: 'flex-start' }}
                          onClick={() => setFTerms((prev) => prev.map((t, i) => i === tIdx ? { type: 'leaf', value: t.operands[0] || null } : t))}
                        >
                          <i className="bi bi-x" /> ( )
                        </button>
                      </div>
                    )}
                  </React.Fragment>
                ))}
                {/* Add / remove top-level term */}
                {fTerms.length < 5 && (
                  <button
                    title="Add another term"
                    style={{ ...pillBtn(false), padding: '2px 10px', flexShrink: 0, alignSelf: 'flex-start', marginTop: 2 }}
                    onClick={() => { setFTerms((prev) => [...prev, { type: 'leaf', value: null }]); setFTermOps((prev) => [...prev, '+']); }}
                  >
                    <i className="bi bi-plus" />
                  </button>
                )}
                {fTerms.length > 2 && (
                  <button
                    title="Remove last term"
                    style={{ ...pillBtn(false), padding: '2px 10px', flexShrink: 0, alignSelf: 'flex-start', marginTop: 2 }}
                    onClick={() => { setFTerms((prev) => prev.slice(0, -1)); setFTermOps((prev) => prev.slice(0, -1)); }}
                  >
                    <i className="bi bi-dash" />
                  </button>
                )}
              </div>
              {/* Type selector + label + action buttons */}
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', gap: 3 }}>
                  <button style={microBtn(fType === 'bar', DARK.gold)} onClick={() => setFType('bar')}>
                    <i className="bi bi-bar-chart-fill" />
                  </button>
                  <button style={microBtn(fType === 'line', DARK.gold)} onClick={() => setFType('line')}>
                    <i className="bi bi-graph-up" />
                  </button>
                </div>
                <input
                  value={fLabel}
                  onChange={(e) => setFLabel(e.target.value)}
                  placeholder="Label (optional)"
                  style={{
                    width: 160,
                    backgroundColor: '#111008',
                    border: `1px solid ${DARK.border}`,
                    borderRadius: 4,
                    color: DARK.goldBright,
                    fontSize: '0.72rem',
                    padding: '4px 8px',
                    outline: 'none',
                  }}
                />
                <button
                  style={{ ...pillBtn(false), opacity: fTerms.flatMap((t) => t.type === 'leaf' ? [t.value] : t.operands).every(Boolean) ? 1 : 0.45 }}
                  disabled={!fTerms.flatMap((t) => t.type === 'leaf' ? [t.value] : t.operands).every(Boolean)}
                  onClick={addFormula}
                >
                  <i className="bi bi-check me-1" />
                  Add
                </button>
                <button
                  style={pillBtn(false)}
                  onClick={() => { setShowFormula(false); setFTerms([{ type: 'leaf', value: null }, { type: 'leaf', value: null }]); setFTermOps(['+']); setFLabel(''); }}
                >
                  <i className="bi bi-x me-1" />
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ── Per-metric type chips ── */}
          {(selectedMetrics.length > 0 || computedMetrics.length > 0) && (
            <div className="d-flex flex-wrap gap-2 mb-3" style={{ paddingTop: 4 }}>
              {selectedMetrics.map((m, i) => {
                const color = CHART_COLORS[i % CHART_COLORS.length];
                return (
                  <div
                    key={m.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      backgroundColor: '#1a1400',
                      border: `1px solid ${DARK.border}`,
                      borderRadius: 20,
                      padding: '3px 8px 3px 6px',
                      fontSize: '0.68rem',
                    }}
                  >
                    {/* Color swatch */}
                    <span
                      style={{
                        width: m.type === 'line' ? 14 : 8,
                        height: m.type === 'line' ? 2 : 8,
                        borderRadius: m.type === 'line' ? 0 : 2,
                        backgroundColor: color,
                        flexShrink: 0,
                        display: 'inline-block',
                      }}
                    />
                    {/* Label */}
                    <span style={{ color, fontWeight: 600 }}>{m.label}</span>
                    {/* Bar / Line toggle */}
                    <button
                      title="Plot as bar"
                      style={microBtn(m.type === 'bar', color)}
                      onClick={() => toggleMetricType(m.value, 'bar')}
                    >
                      <i className="bi bi-bar-chart-fill" />
                    </button>
                    <button
                      title="Plot as line"
                      style={microBtn(m.type === 'line', color)}
                      onClick={() => toggleMetricType(m.value, 'line')}
                    >
                      <i className="bi bi-graph-up" />
                    </button>
                    {/* Y-axis side toggle */}
                    <button
                      title={m.yAxis === 'right' ? 'Move to left axis' : 'Move to right axis'}
                      style={{
                        ...microBtn(m.yAxis === 'right', color),
                        fontFamily: 'monospace',
                        letterSpacing: '-0.05em',
                      }}
                      onClick={() => toggleMetricAxis(m.value)}
                    >
                      {m.yAxis === 'right' ? 'R' : 'L'}
                    </button>
                    {/* Remove */}
                    <button
                      title="Remove"
                      style={{
                        fontSize: '0.65rem',
                        background: 'none',
                        border: 'none',
                        color: DARK.muted,
                        cursor: 'pointer',
                        padding: '0 2px',
                        lineHeight: 1,
                        marginLeft: 1,
                      }}
                      onClick={() => removeMetric(m.value)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}

              {/* ── Computed metric chips ── */}
              {computedMetrics.map((cm, ci) => {
                const colorIdx = selectedMetrics.length + ci;
                const color = CHART_COLORS[colorIdx % CHART_COLORS.length];
                const exprStr = formatExpr(cm.terms, cm.termOps);
                return (
                  <div
                    key={cm.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      backgroundColor: '#1a1400',
                      border: `1px solid #5a3a00`,
                      borderRadius: 20,
                      padding: '3px 8px 3px 6px',
                      fontSize: '0.68rem',
                    }}
                  >
                    {/* Formula expression badge */}
                    <span style={{ color: DARK.muted, fontSize: '0.58rem', fontFamily: 'monospace', flexShrink: 0, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={exprStr}>
                      {exprStr}
                    </span>
                    {/* Color swatch */}
                    <span
                      style={{
                        width: cm.type === 'line' && !cm.stackComponents ? 14 : 8,
                        height: cm.type === 'line' && !cm.stackComponents ? 2 : 8,
                        borderRadius: 2,
                        backgroundColor: color,
                        flexShrink: 0,
                        display: 'inline-block',
                      }}
                    />
                    {/* Label */}
                    <span style={{ color, fontWeight: 600 }}>{cm.label}</span>
                    {/* Bar / Line toggle — hidden when stackComponents is active */}
                    {!cm.stackComponents && (
                      <>
                        <button title="Plot as bar" style={microBtn(cm.type === 'bar', color)} onClick={() => toggleComputedType(cm.id, 'bar')}>
                          <i className="bi bi-bar-chart-fill" />
                        </button>
                        <button title="Plot as line" style={microBtn(cm.type === 'line', color)} onClick={() => toggleComputedType(cm.id, 'line')}>
                          <i className="bi bi-graph-up" />
                        </button>
                      </>
                    )}
                    {/* Stack components toggle — only when formula is a pure sum */}
                    {isAllSum(cm) && (
                      <button
                        title={cm.stackComponents ? 'Collapse to single series' : 'Show operands as stacked bars'}
                        style={microBtn(cm.stackComponents, color)}
                        onClick={() => toggleStackComponents(cm.id)}
                      >
                        <i className="bi bi-bar-chart-steps" />
                      </button>
                    )}
                    {/* Y-axis toggle */}
                    <button
                      title={cm.yAxis === 'right' ? 'Move to left axis' : 'Move to right axis'}
                      style={{ ...microBtn(cm.yAxis === 'right', color), fontFamily: 'monospace', letterSpacing: '-0.05em' }}
                      onClick={() => toggleComputedAxis(cm.id)}
                    >
                      {cm.yAxis === 'right' ? 'R' : 'L'}
                    </button>
                    {/* Remove */}
                    <button
                      title="Remove formula"
                      style={{ fontSize: '0.65rem', background: 'none', border: 'none', color: DARK.muted, cursor: 'pointer', padding: '0 2px', lineHeight: 1, marginLeft: 1 }}
                      onClick={() => removeComputedMetric(cm.id)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Chart area ── */}
          {selectedMetrics.length === 0 ? (
            <div
              style={{
                height: 160,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: DARK.muted,
                border: `1px dashed ${DARK.border}`,
                borderRadius: 6,
              }}
            >
              <i
                className="bi bi-bar-chart"
                style={{ fontSize: '1.8rem', opacity: 0.3, marginBottom: 8 }}
              />
              <div style={{ fontSize: '0.8rem' }}>
                Select one or more metrics above to build your chart.
              </div>
            </div>
          ) : chartData.length === 0 ? (
            <div
              style={{
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: DARK.muted,
                fontSize: '0.8rem',
              }}
            >
              No data available for the selected metrics and time granularity.
            </div>
          ) : (
            renderChart(260)
          )}
        </div>
      )}
      </div>

      {/* ── Fullscreen overlay ── */}
      {fullscreen && chartData.length > 0 && (
        <div
          onClick={() => setFullscreen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: DARK.card,
              border: `1px solid ${DARK.border}`,
              borderRadius: 10,
              width: '92vw',
              maxWidth: 1200,
              padding: '18px 24px 24px',
            }}
          >
            {/* Header: title + close */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ color: DARK.goldBright, fontWeight: 700, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <i className="bi bi-sliders me-2" />
                {activeChartName || 'Custom Chart'}
              </span>
              <button
                onClick={() => setFullscreen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: DARK.muted, fontSize: '1rem' }}
                title="Close"
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginBottom: 14 }}>
              {selectedMetrics.map((m, i) => {
                const color = CHART_COLORS[i % CHART_COLORS.length];
                return (
                  <div key={m.value} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      display: 'inline-block',
                      width: m.type === 'line' ? 18 : 10,
                      height: m.type === 'line' ? 2 : 10,
                      borderRadius: m.type === 'line' ? 0 : 2,
                      backgroundColor: color,
                      flexShrink: 0,
                    }} />
                    <span style={{ color, fontSize: '0.75rem', fontWeight: 600 }}>{m.label}</span>
                    {m.yAxis === 'right' && (
                      <span style={{ color: DARK.muted, fontSize: '0.62rem' }}>(R)</span>
                    )}
                  </div>
                );
              })}
              {computedMetrics.map((cm, ci) => {
                const colorIdx = selectedMetrics.length + ci;
                const color = CHART_COLORS[colorIdx % CHART_COLORS.length];
                const cmLeaves = flatLeaves(cm.terms);
                if (cm.stackComponents && isAllSum(cm)) {
                  return cmLeaves.map((o, oi) => {
                    const col = CHART_COLORS[(colorIdx + oi) % CHART_COLORS.length];
                    return (
                      <div key={`${cm.id}_${oi}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: col, flexShrink: 0 }} />
                        <span style={{ color: col, fontSize: '0.75rem', fontWeight: 600 }}>{shortLabel(o.value)}</span>
                        <span style={{ color: DARK.muted, fontSize: '0.62rem' }}>({cm.label})</span>
                      </div>
                    );
                  });
                }
                return (
                  <div key={cm.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      display: 'inline-block',
                      width: cm.type === 'line' ? 18 : 10,
                      height: cm.type === 'line' ? 2 : 10,
                      borderRadius: cm.type === 'line' ? 0 : 2,
                      backgroundColor: color,
                      flexShrink: 0,
                    }} />
                    <span style={{ color, fontSize: '0.75rem', fontWeight: 600 }}>{cm.label}</span>
                    {cm.yAxis === 'right' && (
                      <span style={{ color: DARK.muted, fontSize: '0.62rem' }}>(R)</span>
                    )}
                  </div>
                );
              })}
            </div>

            {renderChart(Math.min(window.innerHeight * 0.65, 600))}
          </div>
        </div>
      )}
    </>
  );
}
